import moment from 'moment/moment';
import { getLag } from './getLag';

export const updateChain = (
    chain,
    resultPositions,
    plannedParent,
    difference,
    allPositions,
    updateConnectionPositions
) => {
    let result = { ...resultPositions };
    const sortedChain = [...chain]
        .sort(
            (positionA, positionB) =>
                moment(positionA.planned.end_date).valueOf() -
                moment(positionB.planned.end_date).valueOf()
        )
        .reverse();

    const startChain = sortedChain.slice(-1)[0].planned.start_date;
    const endChain = sortedChain[0].planned.end_date;

    const duration = moment.duration(moment(plannedParent.end_date).diff(moment(endChain)));
    const daysDiff = Math.round(+duration.asDays());

    let differenceRemainder = daysDiff - difference;

    if (differenceRemainder > 0) {
        return result;
    }

    differenceRemainder = Math.abs(differenceRemainder);

    const daysFromStartCharter = moment(startChain).diff(plannedParent.start_date, 'days');

    if (daysFromStartCharter >= differenceRemainder) {
        chain.forEach((position) => {
            const planned = {
                ...position.planned,
                start_date: moment(position.planned.start_date)
                    .add(-differenceRemainder, 'days')
                    .format(),
                end_date: moment(position.planned.end_date)
                    .add(-differenceRemainder, 'days')
                    .format(),
            };
            result[position.guid] = { ...result[position.guid], planned };
        });
        differenceRemainder = 0;
    } else {
        chain.forEach((position) => {
            const planned = {
                ...position.planned,
                start_date: moment(position.planned.start_date)
                    .add(-daysFromStartCharter, 'days')
                    .format(),
                end_date: moment(position.planned.end_date)
                    .add(-daysFromStartCharter, 'days')
                    .format(),
            };
            result[position.guid] = { ...result[position.guid], planned };
        });
        differenceRemainder -= daysFromStartCharter;
    }

    let lags = [];

    sortedChain.forEach((position, i) => {
        if (i !== sortedChain.length - 1) {
            const nextPosition = sortedChain[i + 1];

            const lag = getLag(
                position.planned.start_date,
                moment(nextPosition.planned.start_date)
                    .add(+nextPosition.planned.days_count - 1, 'days')
                    .format()
            );

            if (lag > 0) {
                const isFollower = position.connections.some(
                    (connection) =>
                        connection.from === position.guid && connection.to === nextPosition.guid
                );

                let findConnection = position.connections.find(
                    (connection) =>
                        connection.from === position.guid && connection.to === nextPosition.guid
                );

                if (!isFollower) {
                    findConnection = position.connections.find(
                        (connection) =>
                            connection.to === position.guid && connection.from === nextPosition.guid
                    );
                }
                lags.push({ lag: lag, connection: findConnection });
            }
        }
    });

    for (let i = 0; i < lags.length; i++) {
        if (lags.length === 0) {
            break;
        }

        if (lags[i].lag > 0 && differenceRemainder > 0) {
            if (differenceRemainder >= lags[i].lag) {
                const updatedLag = { ...lags[i], lag: 0 };

                let updatedPosition =
                    lags[i].connection.type === 0
                        ? result[lags[i].connection.to]
                        : result[lags[i].connection.from];

                const startDate = moment(updatedPosition.planned.start_date)
                    .add(-lags[i].lag, 'days')
                    .format();

                const endDate = moment(updatedPosition.planned.end_date)
                    .add(-lags[i].lag, 'days')
                    .format();

                const planned = {
                    ...updatedPosition.planned,
                    start_date: startDate,
                    end_date: endDate,
                };
                updatedPosition = {
                    ...updatedPosition,
                    planned,
                };

                differenceRemainder -= lags[i].lag;

                lags[i] = updatedLag;

                result = {
                    ...result,
                    [updatedPosition.guid]: updatedPosition,
                };

                result = updateConnectionPositions(updatedPosition, result, plannedParent);
            } else {
                const updatedLag = { ...lags[i], lag: lags[i].lag - differenceRemainder };

                let updatedPosition =
                    lags[i].connection.type === 0
                        ? allPositions[lags[i].connection.to]
                        : allPositions[lags[i].connection.from];

                const startDate = moment(updatedPosition.planned.start_date)
                    .add(-differenceRemainder, 'days')
                    .format();

                const endDate = moment(updatedPosition.planned.end_date)
                    .add(-differenceRemainder, 'days')
                    .format();

                const planned = {
                    ...updatedPosition.planned,
                    start_date: startDate,
                    end_date: endDate,
                };

                updatedPosition = {
                    ...updatedPosition,
                    planned,
                };

                lags[i] = updatedLag;

                differenceRemainder = 0;

                result = {
                    ...result,
                    [updatedPosition.guid]: updatedPosition,
                };

                result = updateConnectionPositions(updatedPosition, result, plannedParent);
            }
        }
    }

    const updatedChain = sortedChain.map((position) => result[position.guid]);

    updatedChain.forEach((position, index) => {
        if (differenceRemainder === 0) {
            return;
        }

        if (+position.planned.days_count > 1) {
            if (+position.planned.days_count - 1 <= differenceRemainder) {
                const endDate = position.planned.start_date;

                const planned = {
                    ...position.planned,
                    days_count: '1',
                    end_date: endDate,
                };
                const updatedPosition = { ...position, planned };

                differenceRemainder -= Number(position.planned.days_count) - 1;

                result = {
                    ...result,
                    [updatedPosition.guid]: updatedPosition,
                };

                result = updateConnectionPositions(updatedPosition, result, plannedParent);
            } else {
                const countDays = +position.planned.days_count - differenceRemainder;

                const endDate = moment(position.planned.start_date)
                    .add(countDays - 1, 'days')
                    .format();
                const planned = {
                    ...position.planned,
                    days_count: `${countDays}`,
                    end_date: endDate,
                };

                const updatedPosition = { ...position, planned };

                differenceRemainder = 0;

                result = {
                    ...result,
                    [updatedPosition.guid]: updatedPosition,
                };

                result = updateConnectionPositions(updatedPosition, result, plannedParent);
            }
        }
    });

    return result;
};
